/**
 *  React Provider component and hook for application state.
 * @module app
 */

import { ReactNode, useCallback, useContext, useEffect, useMemo, useReducer, useRef } from "react";

import { LOGIN_ROUTE, OAUTH_STATE_KEY } from "../auth/constants";
import { clearClientAuthState, getStoredAccessToken, revokeTokens, validateRedirect } from "../auth/utils";
import { setAuthToken, setUnauthorizedHandler } from "../lib/apiClient";
import { purgeAllChatHistory } from "../lib/chatHistoryStorage";
import { queryClient } from "../lib/queryClient";
import { appReducer } from "../state/appReducer";
import { AppInstance } from "../types";
import { AppContext } from "./Application.context";

/**
 * Custom hook to access the application state from any component.
 *
 * useApp
 * @returns {AppContextValue} The current application state
 * @throws {Error} If used outside of an AppProvider
 * @module app
 * @category Hooks
 *
 * @remarks
 * This hook provides a convenient way to access the application state
 * without directly using `useContext`. It includes a runtime check to
 * ensure it's used within an {@link AppProvider}.
 *
 * @example
 * ```typescript
 * import { useApp } from './Application.provider';
 *
 * function UserProfile() {
 *   const { auth } = useApp();
 *
 *   if (!auth.isAuthenticated) {
 *     return <LoginPrompt />;
 *   }
 *
 *   return <div>Welcome, {auth.user?.name}</div>;
 * }
 * ```
 *
 * @see {@link AppProvider} for the provider component
 * @see {@link AppContextValue} for the shape of the returned value
 */
export const useApp = () => {
  const context = useContext(AppContext);
  if (!context) throw new Error("useApp must be used whithin AppProvider");

  return context;
};

/**
 * Props for the AppProvider component.
 *
 * @interface AppProviderProps
 * @module app
 * @category Components
 */
interface AppProviderProps {
  /** The Application instance to provide to the component tree */
  app: AppInstance;
  /** Child components that will have access to the app context */
  children: ReactNode | ReactNode[];
}

/**
 * React Provider component that wraps the application and provides state management.
 *
 * AppProvider
 * @param {AppProviderProps} props - Component props
 * @returns {JSX.Element} Provider component wrapping children
 * @module app
 * @category Components
 *
 * @remarks
 * The AppProvider is responsible for:
 * - Initializing the application state using `useReducer`
 * - Connecting the Application instance to the React state via `setDispatch`
 * - Providing the state to all child components via {@link AppContext}
 *
 * This component should wrap your entire application, typically in `main.tsx`.
 *
 * @example
 * ```typescript
 * import { Application } from './app/Application';
 * import { AppProvider } from './app/Application.provider';
 * import { App } from './app/Application';
 *
 * const app = new Application(getConfig());
 *
 * createRoot(document.getElementById('root')!).render(
 *   <StrictMode>
 *     <AppProvider app={app}>
 *       <App />
 *     </AppProvider>
 *   </StrictMode>
 * );
 * ```
 *
 * @see {@link Application} for the app instance
 * @see {@link useApp} for accessing the provided state
 * @see {@link appReducer} for state transition logic
 */
/**
 * Login URL for the forced navigation after a 401, carrying the page that
 * failed as `?redirect=` so reauthentication returns there rather than
 * dropping the user on the dashboard.
 *
 * Mirrors what RouteGuard puts in that param, plus the query string — routes
 * like `/action-items?scope=all` are a different page without it. The value is
 * validated here and again by the login route, which rejects anything that is
 * not a same-origin path.
 */
// react-oauth2-code-pkce's flag for a sign-in between logIn() and the token exchange.
const LOGIN_IN_PROGRESS_KEY = "ROCP_loginInProgress";

function buildForcedLoginUrl(): string {
  const { pathname, search } = globalThis.location;
  const target = validateRedirect(`${pathname}${search}`);

  if (!target) {
    return LOGIN_ROUTE;
  }

  return `${LOGIN_ROUTE}?${new URLSearchParams({ redirect: target }).toString()}`;
}

export const AppProvider = ({ app, children }: AppProviderProps) => {
  const [state, dispatch] = useReducer(appReducer, {
    name: app.name,
    env: app.env,
    auth: {
      user: null,
      token: null,
      isAuthenticated: false,
      isLoading: false,
      message: "",
    },
    ui: {
      isSidebarOpen: false,
      activePanelId: "dashboard",
      // `activeEntityId` was hardcoded to the MSW mock case "LD-001".
      // Real seeded cases use UUIDs (see seed_leave_data.py), so that default
      // forced a /cases/LD-001/ 404 on every fresh login against the real API.
      activeEntityId: null,
      activeNavitemId: "",
    },
    version: app.version,
  });

  useEffect(() => {
    app.setDispatch(dispatch);
  }, [app, dispatch]);

  // Token install/eject is owned synchronously by:
  //   • OAuthBridge (login + logout transitions driven by react-oauth2-code-pkce)
  //   • useAuth.logout (manual logout)
  //   • the 401 response handler below
  // Driving it from a useEffect here is unsafe — children-first effect ordering
  // means the parent effect can eject the interceptor immediately after a child
  // re-renders with the new token, racing the first authenticated request.

  // Register a single 401 handler that ejects the interceptor and fires
  // AUTH_LOGOUT so the session is cleared and the user is redirected to login.
  //
  // A 401 means this tab's token is dead — expired, or revoked server-side by
  // a cross-tenant logout — so the tab is sent to /auth/login rather than left
  // to re-render. A full navigation is required, not just the storage purge:
  // react-oauth2-code-pkce only syncs its in-memory token from the `storage`
  // event, which never fires in the tab that wrote it, so `oauthToken` would
  // stay truthy and pin the protected layout on its initializing spinner
  // (routes/_protected/route.tsx).
  //
  // Deliberately NOT /auth/logout. The Django session cookie is shared across
  // tenants, so by the time this tab 401s the session may already belong to
  // ANOTHER tenant that the user just signed into. Hitting the backend logout
  // here would send a request indistinguishable from a real conflict logout —
  // first-party proof and all — and revoke that innocent tenant's fresh
  // tokens. This tab's own credentials are already gone by this point; there
  // is nothing left for a logout call to clean up.
  const hasForcedLogoutRef = useRef(false);

  useEffect(() => {
    setUnauthorizedHandler((failedToken) => {
      // LOA-1613: a 401 can be a stale straggler for a request made with a
      // token another same-origin tab has already replaced with a fresh
      // login's token (localStorage, and therefore ROCP_token, is shared
      // across tabs) — isSigningIn only protects an auth-route tab, not one
      // sitting on a protected route when this race happens. Comparing
      // against what's stored right now, before anything below mutates it,
      // tells that apart from this tab's own session actually dying; acting
      // on the former would revoke or clear a session that isn't dying.
      const currentToken = getStoredAccessToken();

      if (failedToken !== null && currentToken !== null && failedToken !== currentToken) {
        return;
      }

      // Late 401s from the page that just redirected to /auth/login must not
      // wipe the sign-in it started (OAuth state + PKCE verifier): that drops
      // the ?redirect= target and lands the user on the dashboard (LOA-1441).
      // Only a sign-in still in progress keeps its state; rejected tokens are
      // purged everywhere, including after sign-in on /auth/success.
      const isOnAuthRoute = globalThis.location.pathname.startsWith("/auth/");
      const isSigningIn = isOnAuthRoute && localStorage.getItem(LOGIN_IN_PROGRESS_KEY) === "true";
      const pendingAuthState = isOnAuthRoute ? sessionStorage.getItem(OAUTH_STATE_KEY) : null;

      setAuthToken(null);
      if (!isSigningIn) {
        // LOA-1613: this path never reaches routes/auth/logout.tsx (see the
        // "Deliberately NOT /auth/logout" note above), so its own revoke call
        // never fires here. A 401 can mean the access token merely expired
        // while its refresh token is still live — revoke both of THIS tab's
        // own token values (not the ambient session cookie, so the
        // cross-tenant safeguard above is unaffected) before they're wiped.
        void revokeTokens();
        clearClientAuthState();
      }
      sessionStorage.clear();
      if (pendingAuthState !== null) {
        sessionStorage.setItem(OAUTH_STATE_KEY, pendingAuthState);
      }
      // Persisted ARIES transcripts outlive localStorage sweeps by design, so
      // drop them here as useAuth.logout does — a revoked session must not
      // leave transcripts the next user on this origin can resume. Since this
      // path no longer routes through /auth/logout, this is the only purge.
      purgeAllChatHistory();
      queryClient.clear();
      dispatch({ type: "AUTH_LOGOUT" });

      // Parallel 401s share one navigation; auth routes make no API calls, so
      // this cannot loop back on itself.
      if (hasForcedLogoutRef.current || isOnAuthRoute) {
        return;
      }

      hasForcedLogoutRef.current = true;
      globalThis.location.href = buildForcedLoginUrl();
    });
  }, [dispatch]);

  // Stable across re-renders (dispatch never changes) — consumers that key a
  // useMemo/useCallback off these must not see a new function identity every
  // time `state` updates, or they'll spuriously reconstruct on every render.
  const toggleSidebar = useCallback(() => {
    dispatch({ type: "UI_TOGGLE_SIDEBAR" });
  }, []);

  const setActivePanel = useCallback((panelId: string) => {
    dispatch({ type: "UI_SET_ACTIVE_PANEL", payload: { panelId } });
  }, []);

  const setActiveEntity = useCallback((entityId: string) => {
    dispatch({ type: "UI_SET_ACTIVE_ENTITY", payload: { entityId } });
  }, []);

  const setActiveNavItem = useCallback((navitemId: string) => {
    dispatch({ type: "UI_SET_ACTIVE_NAVITEM", payload: { navitemId } });
  }, []);

  const value = useMemo(
    () => ({
      ...state,
      toggleSidebar,
      setActivePanel,
      setActiveEntity,
      setActiveNavItem,
      dispatch,
    }),
    [state, toggleSidebar, setActivePanel, setActiveEntity, setActiveNavItem],
  );

  return <AppContext.Provider value={value}>{children}</AppContext.Provider>;
};
